import { useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import type { CarSubscriptionEarnings } from '@/api/partners/cars/use-car-subscription-earnings';
import { formatNumber } from '@/core';
import { Card, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Info } from '@/ui/icons';
import { SegmentedControl } from '@/ui/segmented-control';

const EARNINGS_PERIODS = [
  { key: 'weekly', label: 'Weekly', suffix: '/wk' },
  { key: 'monthly', label: 'Monthly', suffix: '/mo' },
  { key: '6Months', label: '6-month', suffix: '/6mo' },
] as const;

type EarningsPeriodKey = (typeof EARNINGS_PERIODS)[number]['key'];

export function SubscriptionEarningsCard({
  earnings,
  style,
}: {
  earnings?: CarSubscriptionEarnings;
  style?: StyleProp<ViewStyle>;
}) {
  const { theme } = useStyles();
  const [selectedPeriod, setSelectedPeriod] =
    useState<EarningsPeriodKey>('monthly');

  const activePeriod = EARNINGS_PERIODS.find((p) => p.key === selectedPeriod)!;

  const periodData = earnings?.[selectedPeriod] ?? {};

  const tiers = Object.keys(periodData)
    .map(Number)
    .sort((a, b) => a - b);

  const highlightedTier = selectedPeriod === 'weekly' ? 500 : 1500;

  return (
    <Card style={style}>
      <View style={{ padding: 16, gap: 16 }}>
        <View style={{ gap: 8 }}>
          <Text type="title2">What You'd Earn By Mileage Tier</Text>
        </View>

        <Alert
          type={AlertType.WARNING}
          title="See your car earnings below, your exact monthly earnings will depend on how much your guest drives."
          icon={<Info />}
        />
      </View>

      <View style={{ padding: 16, gap: 8 }}>
        <View
          style={{
            gap: 16,
            padding: 16,
            borderRadius: 16,
            backgroundColor: theme.colors.background_1,
          }}
        >
          <SegmentedControl
            tab1Label="Weekly"
            tab2Label="Monthly"
            tab3Label="6-month"
            onPressTab1={() => {
              setSelectedPeriod('weekly');
            }}
            onPressTab2={() => {
              setSelectedPeriod('monthly');
            }}
            onPressTab3={() => {
              setSelectedPeriod('6Months');
            }}
            style={{
              backgroundColor: theme.colors.card.default.backgroundColor,
            }}
            activeIdx={1}
          />

          <View
            style={{
              overflow: 'hidden',
            }}
          >
            {tiers.map((tier) => {
              const isActive = tier === highlightedTier;
              const earningsForTier =
                periodData[tier.toString()]?.estimatedEarnings ?? 0;

              return (
                <View
                  key={tier}
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 16,
                    backgroundColor: isActive
                      ? theme.colors.alert.brand.backgroundColor
                      : 'transparent',
                    gap: 16,
                    borderRadius: isActive ? 16 : 0,
                    borderWidth: isActive ? 1 : 0,
                    borderColor: isActive
                      ? theme.colors.alert.brand.border
                      : 'transparent',
                  }}
                >
                  <View style={{ gap: 2, flexShrink: 1 }}>
                    <Text type="bodyRegular">{formatNumber(tier)} mi</Text>

                    {isActive ? (
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        most subscriptions run{' '}
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.icon.brandColor }}
                        >
                          ~4 months
                        </Text>{' '}
                        at this tier
                      </Text>
                    ) : null}
                  </View>

                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'baseline',
                      gap: 2,
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={
                        isActive
                          ? { color: theme.colors.icon.brandColor }
                          : undefined
                      }
                    >
                      ${formatNumber(Math.round(earningsForTier))}
                    </Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {activePeriod.suffix}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </View>
    </Card>
  );
}
